import {
  EnvironmentProviders,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { catchError, of, take } from 'rxjs';
import { AuthFacade } from './auth-facade';

/**
 * Secondary, lighter half of #31536. The {@link IssuerAwareAuthInterceptor}
 * is the real recovery path: it silently refreshes the token and retries
 * once on any 401. This warms the Auth0 access token at page load so the
 * FIRST API call after an overnight idle-and-reload already carries a fresh
 * token, sparing it the 401 -> refresh -> retry round-trip.
 *
 * Auth0 only: an Entra session already mints a freshly-refreshed token per
 * request via MSAL `acquireTokenSilent`, so there is nothing to pre-warm and
 * touching that path would risk the #30531 no-account semantics.
 *
 * Fire-and-forget by contract. It never blocks bootstrap (the initializer
 * returns void, so Angular does not await it) and never surfaces an error (a
 * failed warm is a no-op; the interceptor still recovers on the first real
 * 401). A plain `getAccessToken()` is deliberate: it refreshes only if the
 * cached token is expired, which is exactly the "lighter" pre-warm intended,
 * unlike the interceptor's forced `ignoreCache` refresh.
 */
export function proactivelyRefreshAuth0Token(facade: AuthFacade): void {
  if (facade.issuer === 'entra') {
    return;
  }
  facade
    .getAccessToken()
    .pipe(
      take(1),
      catchError(() => of(null))
    )
    .subscribe();
}

/**
 * Registers {@link proactivelyRefreshAuth0Token} as an app initializer so an
 * Auth0 session pre-warms its access token on page load. Returns
 * `EnvironmentProviders`, which drop straight into an app's `providers`.
 */
export function provideAuth0ProactiveTokenRefresh(): EnvironmentProviders {
  return provideAppInitializer(() =>
    proactivelyRefreshAuth0Token(inject(AuthFacade))
  );
}
